import type { FragmentType } from 'src/gql';
import type { Deprecated_HandleItemUpdate } from 'src/types/account';
import { useMemo, useState } from 'react';
import { InputErrorMessage } from '@savvy-kit/components/input-error-message';
import { Label } from '@savvy-kit/components/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@savvy-kit/components/select';
import { cn } from '@savvy-kit/lib/utils';
import { DateField } from '@savvy-kit/patterns/date-field';
import InputField from '@savvy-kit/patterns/input-field';
import { isNil } from 'es-toolkit';
import { useFormik } from 'formik';
import { DateTime } from 'luxon';
import MonetaryInputTextField from 'src/components/mui/monetary-input-text-field';
import TrackedButton from 'src/components/tracked/tracked-button';
import { LIABILITY_TYPE_DISPLAY_NAME, LIABILITY_TYPES_MANUAL_ADD } from 'src/constants/account-link';
import { UpdateType } from 'src/constants/portfolio';
import { graphql, unmaskFragment } from 'src/gql';
import { formatCurrencyForSubmit } from 'src/utils/currency';
import { removeFalsey } from 'src/utils/object-manipulation';
import { getFormattedEOD, isValidDate } from 'src/utils/validation';
import { date as yupDate, number as yupNumber, object as yupObject, string as yupString } from 'yup';

import type { ID } from '@savvy/utils/id';
import { ASSET_CLASS_REAL_ESTATE } from '@savvy/constants/asset-classes';
import { LIABILITY_TYPE_MORTGAGE } from '@savvy/constants/liability-types';
import { useIsSmallScreen } from '@savvy/deprecated-shared/hooks/use-is-small-screen';
import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

import RemoveLiabilityDialog from '../portfolio/liability/remove-liability-dialog';

const MAX_STRING_LENGTH = 255;

export const LiabilityFormFragment = graphql(/* GraphQL */ `
  fragment LiabilityForm on Liability {
    id
    name
    type
    currentBalance {
      amount
      currencyCode
    }
    originalBalance {
      amount
      currencyCode
    }
    acquiredAt
    timeToMaturity
    annualInterestPercent
    providerName
  }
`);

export const LiabilityFormAssetFragment = graphql(/* GraphQL */ `
  fragment LiabilityFormAsset on Asset {
    id
    assetClass
  }
`);

const CREATE_LIABILITY = graphql(/* GraphQL */ `
  mutation CreateLiability($input: CreateLiabilityInput!) {
    createLiability(input: $input) {
      liability {
        id
      }
    }
  }
`);

const UPDATE_LIABILITY = graphql(/* GraphQL */ `
  mutation UpdateLiability($input: UpdateLiabilityInput!) {
    updateLiability(input: $input) {
      liability {
        ...LiabilityForm
      }
    }
  }
`);

const VALID_DECIMAL_INPUT_REGEX = /^[1-9]\d*(?:\.)?\d*$/;
const VALID_DECIMAL_NUMBER_REGEX = /^[1-9]\d*(?:\.\d+)?$/;

type LiabilityFormProps = {
  readonly assetFrag?: FragmentType<typeof LiabilityFormAssetFragment>;
  readonly onSuccess: Deprecated_HandleItemUpdate;
  readonly liabilityFrag?: FragmentType<typeof LiabilityFormFragment>;
  readonly isEditMode: boolean;
  readonly clientUserId?: ID;
  readonly hiddenFromClient?: boolean;
  readonly assetId?: number | null;
  readonly isMortgage?: boolean;
};

function toAcquiredAtDate(stored: string): Date | undefined {
  if (!stored) return undefined;
  const parsed = DateTime.fromISO(stored);
  return parsed.isValid ? parsed.toJSDate() : undefined;
}

function buttonText(isEditMode: boolean, isRealEstateAssociatedMortgage: boolean) {
  if (isRealEstateAssociatedMortgage) {
    if (isEditMode) {
      return 'Update mortgage';
    }
    return 'Add mortgage';
  }
  if (isEditMode) {
    return 'Save liability';
  }
  return 'Add liability';
}

export default function LiabilityForm({
  assetFrag,
  isEditMode,
  onSuccess,
  liabilityFrag,
  hiddenFromClient,
  clientUserId,
  assetId,
  isMortgage,
}: LiabilityFormProps) {
  const liability = unmaskFragment(LiabilityFormFragment, liabilityFrag);
  const asset = unmaskFragment(LiabilityFormAssetFragment, assetFrag);
  const [createLiability] = useMutationWithErrorLogging(CREATE_LIABILITY);
  const [updateLiability] = useMutationWithErrorLogging(UPDATE_LIABILITY);
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
  const isSmallScreen = useIsSmallScreen();
  const today = useMemo(() => new Date(), []);

  const isRealEstateAssociatedMortgage = isMortgage || asset?.assetClass === ASSET_CLASS_REAL_ESTATE;

  const getGraphqlInputFromForm = (isUpdate: boolean) => {
    return {
      acquiredAt: formik.values.acquiredAt,
      currencyCode: 'USD',
      currentBalance: formatCurrencyForSubmit(formik.values.currentBalance),
      name: formik.values.name,
      originalBalance: formatCurrencyForSubmit(formik.values.originalBalance),
      timeToMaturity: parseInt(formik.values.timeToMaturity),
      type: formik.values.type,
      ...removeFalsey({
        hiddenFromClient,
        assetId: (() => {
          if (isUpdate) {
            return null;
          }

          if (asset) {
            return Number(asset.id);
          }

          return assetId;
        })(),
        providerName: formik.values.providerName,
        annualInterestPercent: formik.values.annualInterestPercent,
        userId: isUpdate ? null : clientUserId,
      }),
    };
  };

  const handleUpdateLiability = async () => {
    await updateLiability({
      variables: {
        // @ts-expect-error - FIXME: This is using the wrong type
        input: {
          id: liability?.id ?? '',
          ...getGraphqlInputFromForm(true),
        },
      },
    });
    void onSuccess({
      id: liability?.id ?? '',
      type: isRealEstateAssociatedMortgage ? UpdateType.UPDATE_REAL_ESTATE : UpdateType.UPDATE_LIABILITY,
    });
  };
  const handleCreateLiability = async () => {
    const input = getGraphqlInputFromForm(false);
    const currentBalance = input.currentBalance;
    const originalBalance = input.originalBalance;
    if (isNil(currentBalance) || isNil(originalBalance)) {
      return;
    }
    const res = await createLiability({
      // @ts-expect-error Will self-resolve with better typing
      variables: { input: { ...input, currentBalance, originalBalance } },
    });
    void onSuccess({
      id: res.data?.createLiability?.liability.id ?? '',
      type: isRealEstateAssociatedMortgage ? UpdateType.CREATE_REAL_ESTATE : UpdateType.CREATE_LIABILITY,
    });
  };

  const initialValues = {
    name: isRealEstateAssociatedMortgage ? 'Mortgage' : '',
    providerName: '',
    type: isRealEstateAssociatedMortgage ? LIABILITY_TYPE_MORTGAGE : '',
    currentBalance: '',
    originalBalance: '',
    acquiredAt: '',
    timeToMaturity: '',
    annualInterestPercent: '',
    submit: null,
  };

  if (!isNil(liability)) {
    Object.assign(initialValues, {
      name: liability.name || '',
      providerName: liability.providerName || '',
      type: liability.type,
      currentBalance: liability.currentBalance.amount,
      originalBalance: liability.originalBalance.amount,
      acquiredAt: liability.acquiredAt || '',
      timeToMaturity: liability.timeToMaturity || '',
      annualInterestPercent: liability.annualInterestPercent || '',
    });
  }

  const dateString = '1900-01-01';
  const dateMin = DateTime.fromISO(dateString).toString();
  const formik = useFormik({
    initialValues,
    validationSchema: yupObject({
      name: yupString()
        .max(MAX_STRING_LENGTH, `Name must be at most ${MAX_STRING_LENGTH} characters`)
        .required('Name is required'),

      providerName: yupString().max(MAX_STRING_LENGTH, `Name must be at most ${MAX_STRING_LENGTH} characters`),
      type: yupString().required('Liability type is required'),
      currentBalance: yupNumber().min(0, 'Current balance cannot be negative').required('Current balance is required'),
      originalBalance: yupNumber()
        .positive()
        .required(
          isRealEstateAssociatedMortgage ? 'Original mortgage amount is required' : 'Original amount is required',
        ),
      acquiredAt: yupDate()
        .required('Start date is required')
        .min(dateString, `Date must be later than ${dateMin}`)
        .max(getFormattedEOD(), "Date can't be in the future."),
      timeToMaturity: yupNumber().integer().positive().min(1, 'Maturity time should not be zero'),
      annualInterestPercent: yupString().test(
        'is-valid-decimal',
        'Please enter a valid percentage',
        (value) => !value || VALID_DECIMAL_NUMBER_REGEX.test(value),
      ),
    }),
    onSubmit: async (): Promise<void> => {
      if (isEditMode && liability) {
        await handleUpdateLiability();
      } else {
        await handleCreateLiability();
      }
    },
  });

  return (
    <form noValidate onSubmit={formik.handleSubmit}>
      {!isRealEstateAssociatedMortgage && (
        <InputField
          id="name"
          autoFocus={!isEditMode}
          containerClassName="mb-2"
          error={Boolean(formik.touched.name && formik.errors.name && formik.dirty)}
          errorMessage={formik.errors.name}
          label="Name"
          name="name"
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          type="text"
          value={formik.values.name}
        />
      )}
      {!isRealEstateAssociatedMortgage && (
        <div className="flex flex-1 flex-col gap-2">
          <Label htmlFor="type">Type</Label>
          <Select
            value={formik.values.type}
            items={LIABILITY_TYPES_MANUAL_ADD.map((key) => ({ value: key, label: LIABILITY_TYPE_DISPLAY_NAME[key] }))}
            onValueChange={(value) => formik.setFieldValue('type', value)}
          >
            <SelectTrigger
              id="type"
              className="w-full"
              onBlur={() => formik.setFieldTouched('type', true)}
              aria-describedby={formik.touched.type && formik.errors.type ? 'liability-type-error' : undefined}
            >
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent>
              {LIABILITY_TYPES_MANUAL_ADD.map((key) => (
                <SelectItem value={key} key={key}>
                  {LIABILITY_TYPE_DISPLAY_NAME[key]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {formik.touched.type && formik.errors.type ? (
            <p id="liability-type-error" className="text-destructive text-sm">
              {formik.errors.type}
            </p>
          ) : null}
        </div>
      )}
      <MonetaryInputTextField
        error={Boolean(formik.touched.currentBalance && formik.errors.currentBalance)}
        autoFocus={isRealEstateAssociatedMortgage && !isEditMode}
        errorMessage={formik.errors.currentBalance}
        label="Current balance"
        name="currentBalance"
        onBlur={formik.handleBlur}
        formik={formik}
        value={formik.values.currentBalance}
      />
      <MonetaryInputTextField
        error={Boolean(formik.touched.originalBalance && formik.errors.originalBalance)}
        errorMessage={formik.errors.originalBalance}
        label="Original amount"
        name="originalBalance"
        onBlur={formik.handleBlur}
        formik={formik}
        value={formik.values.originalBalance}
      />
      <DateField
        id="acquiredAt"
        label="Start date"
        maxDate={today}
        onChange={(value) => {
          // If value is not a valid date object, formik will show a very ugly
          // Error message that only the developer should see.
          // Setting an empty string instead of an invalid date object can avoid
          // That problem.
          const validDate = isValidDate(value);
          void formik.setFieldValue('acquiredAt', validDate ? DateTime.fromJSDate(value).toISODate() : '', validDate);
        }}
        onBlur={() => formik.setFieldTouched('acquiredAt', true)}
        value={toAcquiredAtDate(formik.values.acquiredAt)}
        error={Boolean(formik.touched.acquiredAt && formik.errors.acquiredAt)}
        errorMessage={
          formik.touched.acquiredAt && formik.errors.acquiredAt ? String(formik.errors.acquiredAt) : undefined
        }
      />
      {isRealEstateAssociatedMortgage && (
        <>
          <InputField
            id="timeToMaturity"
            containerClassName="mt-4 mb-2"
            error={Boolean(formik.touched.timeToMaturity && formik.errors.timeToMaturity)}
            errorMessage={formik.errors.timeToMaturity}
            label="Duration (years) (optional)"
            name="timeToMaturity"
            appendInputAddon="Years"
            onBlur={formik.handleBlur}
            onChange={(e) => {
              const value = e.target.value;
              // Only allow integer >= 1
              if (value === '' || /^[1-9]\d*$/.test(value)) {
                formik.handleChange(e);
              }
            }}
            type="text"
            inputMode="numeric"
            value={formik.values.timeToMaturity}
          />
          <InputField
            id="annualInterestPercent"
            containerClassName="mt-4 mb-2"
            error={Boolean(formik.touched.annualInterestPercent && formik.errors.annualInterestPercent)}
            errorMessage={formik.errors.annualInterestPercent}
            label="Annual interest rate (optional)"
            name="annualInterestPercent"
            appendInputAddon="%"
            onBlur={formik.handleBlur}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || VALID_DECIMAL_INPUT_REGEX.test(value)) {
                // Eagerly remove error messages when the current input is valid.
                // Only show error when out of focus.
                const shouldValidate = value === '' || VALID_DECIMAL_NUMBER_REGEX.test(value);
                void formik.setFieldValue('annualInterestPercent', value, shouldValidate);
              }
            }}
            type="text"
            value={formik.values.annualInterestPercent}
          />
          <InputField
            id="providerName"
            containerClassName="mt-4 mb-2"
            error={Boolean(formik.touched.providerName && formik.errors.providerName)}
            errorMessage={formik.errors.providerName}
            label="Mortgage provider (optional)"
            name="providerName"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            type="text"
            value={formik.values.providerName}
          />
        </>
      )}
      {formik.errors.submit && (
        <div className="mt-6">
          <InputErrorMessage message={formik.errors.submit} />
        </div>
      )}
      {isRemoveDialogOpen && liability && (
        <RemoveLiabilityDialog
          // @ts-expect-error This will self-resolve with better typing in this component
          liabilityFrag={liability}
          onClose={() => {
            setIsRemoveDialogOpen(false);
          }}
          onRemove={onSuccess}
        />
      )}
      <div className={cn('flex gap-4 justify-end mt-4 mb-6', isSmallScreen ? 'flex-col' : 'flex-row')}>
        {isEditMode && isRealEstateAssociatedMortgage && liability && (
          <TrackedButton
            elementName="liability-form-delete-mortgage-button"
            disabled={formik.isSubmitting}
            onClick={() => {
              setIsRemoveDialogOpen(true);
            }}
            variant="destructive"
          >
            Delete mortgage
          </TrackedButton>
        )}
        <TrackedButton
          elementName={isEditMode ? 'liability-form-update-mortgage-button' : 'liability-form-add-mortgage-button'}
          disabled={formik.isSubmitting || !formik.isValid || (!formik.dirty && !isRealEstateAssociatedMortgage)}
          className={isSmallScreen ? 'w-full' : undefined}
          type="submit"
        >
          {buttonText(isEditMode, isRealEstateAssociatedMortgage)}
        </TrackedButton>
      </div>
    </form>
  );
}
